import React, { Fragment, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useEffectOnce, useSelect } from '@chilecompra/react-kit'
import { Select, Skeleton, Toast, Typography } from '@chilecompra/react-kit/components'

import GoodsAndServicesRow from './SearchGoodsAndServicesByCategory.row'
import { onAddProductSuccess } from '../AddProductsServices/AddProductsServices.actions'
import {
  onGetCategoriesLevelTwoThunk,
  onGetCategoriesThunk,
  onGetGoodsAndServicesByCategoryThunk
} from './SearchGoodsAndServicesByCategory.actions'
import {
  ArrowRightIcon,
  ArrowDownIcon,
  Divider,
  Root,
  Grid,
  SkeletonWrapper,
  TreeView,
  TreeItem
} from './SearchGoodsAndServicesByCategory.styles'

import { topPage } from '../../modules/utils/topPage'
import { inIframe } from '../../modules/utils/inIframe'

/**
 * The SearchGoodsAndServicesByCategory's container.
 */
const SearchGoodsAndServicesByCategory = () => {
  const {
    categories,
    categoriesLevelTwo,
    error,
    gettingCatergories,
    gettingCatergoriesLevelTwo,
    gettingGoodsAndServices,
    goodsAndServices
  } = useSelector(state => state.searchGoodsAndServicesByCategory)
  const { productsSaved } = useSelector(state => state.addProductsService)

  const dispatch = useDispatch()

  const isInIframe = useMemo(() => {
    return inIframe()
  }, [])

  const [categoryName, setCategoryName] = useState('')

  const handleOnChangeCategory = updatedValue => {
    setCategoryName('')
    dispatch(onGetCategoriesLevelTwoThunk({ categoryId: updatedValue }))
  }

  const { onChange: handleCategoryChange, value: categoryValue } = useSelect({
    changeCallback: handleOnChangeCategory
  })

  const handleGetGoodAndServices = (categoryId, categoryName) => () => {
    setCategoryName(categoryName)
    dispatch(onGetGoodsAndServicesByCategoryThunk({ categoryId, categoryName }))
  }

  const handleAdd = (id, name, categoryId) => () => {
    const productKey = (Math.random() + 1).toString(36).substring(7)
    const newProduct = {
      productId: id,
      productKey,
      productName: name,
      categoryId,
      quantity: null,
      unit: null,
      detail: ''
    }
    dispatch(onAddProductSuccess({ newProduct }))
  }

  useEffectOnce(() => {
    topPage(isInIframe)
    dispatch(onGetCategoriesThunk())
  })

  return (
    <Root>
      <Grid container>
        <Grid item>
          <Select
            label="Clasificador"
            loading={gettingCatergories}
            onChange={handleCategoryChange}
            options={categories || []}
            value={categoryValue}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item>
          {gettingCatergoriesLevelTwo && (
            <SkeletonWrapper>
              <Skeleton height={35} variant="text" width={300} />
              <Skeleton height={35} margin="0 0 0 20px" variant="text" width={300} />
              <Skeleton height={35} margin="0 0 0 20px" variant="text" width={300} />
              <Skeleton height={35} variant="text" width={300} />
              <Skeleton height={35} margin="0 0 0 20px" variant="text" width={300} />
              <Skeleton height={35} margin="0 0 0 20px" variant="text" width={300} />
            </SkeletonWrapper>
          )}
          {!gettingCatergoriesLevelTwo && (
            <TreeView
              slotProps={{
                expandIcon: { component: ArrowRightIcon },
                collapseIcon: { component: ArrowDownIcon }
              }}
            >
              {categoriesLevelTwo.map((level2, indexLevel2) => (
                <TreeItem
                  label={
                    <Typography variant="body2" fontWeight="bold">
                      {level2.name}
                    </Typography>
                  }
                  key={level2.value}
                  itemId={level2.value}
                  nodeId={indexLevel2}
                >
                  {level2.categories.map((category, indexCategory) => (
                    <TreeItem
                      key={category.id}
                      label={
                        <Typography variant="body2" fontWeight="bold">
                          {category.name}
                        </Typography>
                      }
                      itemId={category.id}
                      nodeId={indexCategory}
                      onClick={handleGetGoodAndServices(category.id, category.name)}
                    />
                  ))}
                </TreeItem>
              ))}
            </TreeView>
          )}
        </Grid>
      </Grid>
      <Divider margin="32px 0 24px" />
      <Grid container>
        <Grid item>
          <Typography color="black1" fontWeight="bold" variant="h4">
            Selecciona los productos a agregar
          </Typography>
        </Grid>
      </Grid>
      {gettingGoodsAndServices && (
        <Grid container margin="13px 0">
          <Grid item>
            <SkeletonWrapper>
              <Skeleton height={35} width={150} />
              <Skeleton height={50} variant="text" />
              <Skeleton height={50} variant="text" />
              <Skeleton height={50} variant="text" />
              <Skeleton height={50} variant="text" />
            </SkeletonWrapper>
          </Grid>
        </Grid>
      )}
      {!gettingGoodsAndServices && goodsAndServices?.length > 0 && (
        <>
          <Grid container margin="16px 0 0">
            <Grid item>
              <Typography color="black1" fontWeight="bold" variant="body2">
                {categoryName}
              </Typography>
            </Grid>
          </Grid>
          <Grid container margin="13px 0">
            <Grid item>
              {goodsAndServices.map(goodAndService => (
                <Fragment key={goodAndService.value}>
                  <GoodsAndServicesRow
                    categoryId={goodAndService.categoryId}
                    handleAdd={handleAdd}
                    id={goodAndService.value}
                    isAdded={productsSaved.some(added => added.productId === goodAndService.value)}
                    key={goodAndService.value}
                    name={goodAndService.name}
                  />
                  <Divider key={goodAndService.value} />
                </Fragment>
              ))}
            </Grid>
          </Grid>
        </>
      )}
      <Grid container margin="16px 0 0">
        {!gettingCatergories && !gettingCatergoriesLevelTwo && !gettingGoodsAndServices && error && (
          <Toast severity="error" title="Lo sentimos, estamos presentando problemas con nuestro servidor.">
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        )}
      </Grid>
    </Root>
  )
}

export default SearchGoodsAndServicesByCategory
